import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { generateQuestions } from "../../utils/questionsService";
import { Brain, Zap } from "lucide-react";

const ProfileInterview = () => {
  const { profileId } = useParams();
  const [difficulty, setDifficulty] = useState("beginner");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleStart = async () => {
    setLoading(true);
    const data = await generateQuestions(profileId, difficulty);
    setLoading(false);
    navigate(`/interview/${profileId}/questions`, {
      state: { data, profileId, difficulty },
    });
  };

  return (
    <ScreenWrapper>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Title>
          <Brain size={40} color="#c59fff" /> &nbsp;
          <span>{profileId.toUpperCase()} Interview Prep</span>
        </Title>
      </motion.div>

      <Card
        as={motion.div}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Label>Select Your Difficulty Level</Label>
        <SelectBox>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
          >
            <option value="beginner">🌱 Beginner</option>
            <option value="experienced">⚙️ Experienced</option>
            <option value="expert">🚀 Expert</option>
          </select>
        </SelectBox>

        <GradientButton onClick={handleStart} disabled={loading}>
          <Zap size={18} />
          &nbsp; {loading ? "Generating..." : "Start Interview"}
        </GradientButton>
      </Card>
    </ScreenWrapper>
  );
};

export default ProfileInterview;

// ---------------- Styled Components ----------------

const ScreenWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  font-size: 2rem;
  font-weight: 600;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 40px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 20px;
  padding: 50px 40px;
  max-width: 450px;
  width: 90%;
  text-align: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 25px rgba(155, 92, 255, 0.2);
  transition: 0.3s;

  &:hover {
    border-color: rgba(197, 159, 255, 0.6);
    transform: translateY(-5px);
  }
`;

const Label = styled.p`
  font-size: 1.1rem;
  color: #c59fff;
  margin-bottom: 15px;
  font-weight: 500;
`;

const SelectBox = styled.div`
  margin-bottom: 35px;

  select {
    width: 100%;
    padding: 12px 16px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(155, 92, 255, 0.4);
    color: #fff;
    font-size: 1rem;
    outline: none;
    cursor: pointer;
    transition: 0.3s;

    &:hover {
      border-color: #c59fff;
    }

    option {
      background: #0b0617;
      color: #fff;
    }
  }
`;

const GradientButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  box-shadow: 0 0 20px rgba(155, 92, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
